import React, { useCallback, useEffect, useRef, useState } from "react";
import classNames from "classnames/bind";
import storiesStyles from "./StoriesSubSlider.module.scss";
import { SwiperSlide } from "swiper/react";
import { useStoriesProviderData } from "../../../providers/storiesProvider";
import { Swiper as SwiperClass } from "swiper/types";
import { StorySlide } from "@core/types";
import { useStoryButton } from "../../../utils/useStoryButton";

type SlideImageTypeType = {
  buttonSlot?: StorySlide["button"];
  src: string;
  isActive: boolean;
  swiper: SwiperClass;
};

const cx = classNames.bind(storiesStyles);
const timeOut = 2500;

const SlideImageType = ({ buttonSlot, src, isActive, swiper }: SlideImageTypeType) => {
  const {
    nestedSlider,
    startPaginationAnimating,
    setTimer,
    setIsSliderPlaying,
    clearTimer,
    slideToNext,
    setBulletToStartPosition,
    setIsSliderLoading,
  } = useStoriesProviderData();
  const { handleStoryButtonClick } = useStoryButton(buttonSlot);
  const [imageUrl, setImageUrl] = useState("");
  const isLoadedBefore = useRef(false);
  let loadingMinTimer: ReturnType<typeof setTimeout> | null = null;

  const handleImgError = useCallback(
    (e: any) => {
      console.log(e, "error");
      // e.currentTarget.src = defaultPlaceholder;
    },
    [src]
  );

  const startSliding = () => {
    if (!swiper?.pagination?.bullets?.length) {
      return;
    }
    setIsSliderLoading(false);
    startPaginationAnimating(swiper, timeOut);
    setIsSliderPlaying(true);

    if (swiper.isEnd) {
      setTimer(() => {
        clearTimer();
        slideToNext();
        if (!nestedSlider?.destroyed) {
          setBulletToStartPosition(swiper);
        }
      }, timeOut);
      return;
    }
    setTimer(() => {
      clearTimer();
      slideToNext();
    }, timeOut);
  };

  useEffect(() => {
    if (loadingMinTimer) {
      clearTimeout(loadingMinTimer);
      loadingMinTimer = null;
    }
    if (!isActive) {
      return;
    }

    setIsSliderPlaying(false);
    setIsSliderLoading(true);

    if (isLoadedBefore.current) {
      startSliding();
      return;
    }

    const img = new window.Image();

    img.onload = function (e) {
      if (nestedSlider?.destroyed) {
        return;
      }
      setImageUrl(img.src);
      isLoadedBefore.current = true;
      loadingMinTimer = setTimeout(() => {
        startSliding();
      }, 500);
    };

    img.src = src;

    return () => {
      img.src = "";
      if (loadingMinTimer) {
        clearTimeout(loadingMinTimer);
        loadingMinTimer = null;
      }
    };
  }, [isActive, swiper]);

  const handleClick = (e: React.MouseEvent) => {
    handleStoryButtonClick();
  };

  return (
    <SwiperSlide className={cx("slider__item")}>
      <img loading="lazy" className={cx("slider__img")} src={imageUrl} onError={handleImgError} alt="" />
      {!!buttonSlot && (
        <button onClick={handleClick} className={cx("link-button")}>
          {buttonSlot.text}
        </button>
      )}
    </SwiperSlide>
  );
};

SlideImageType.displayName = "SwiperSlide";

export { SlideImageType };
